import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";

const NetworkStatusContext = createContext();

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      "useNetworkStatus must be used within a NetworkStatusProvider"
    );
  }
  return context;
};

export const NetworkStatusProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState("unknown");
  const [connectionSpeed, setConnectionSpeed] = useState("unknown");
  const [lastSeen, setLastSeen] = useState(new Date());

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastSeen(new Date());
      toast.success("Connection restored!", {
        duration: 3000,
        position: "top-right",
        style: {
          background: "#10B981",
          color: "#fff",
        },
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setLastSeen(new Date());
      toast.error("You are offline. Some features may be limited.", {
        duration: 5000,
        position: "top-right",
        style: {
          background: "#EF4444",
          color: "#fff",
        },
      });
    };

    const updateConnectionInfo = () => {
      if ("connection" in navigator) {
        const connection = navigator.connection;
        setConnectionType(connection.effectiveType || "unknown");
        setConnectionSpeed(
          connection.downlink ? `${connection.downlink} Mbps` : "unknown"
        );
      }
    };

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    if ("connection" in navigator) {
      navigator.connection.addEventListener("change", updateConnectionInfo);
      updateConnectionInfo(); // Initial call
    }

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if ("connection" in navigator) {
        navigator.connection.removeEventListener(
          "change",
          updateConnectionInfo
        );
      }
    };
  }, []);

  // Monitor connection quality
  useEffect(() => {
    if (!isOnline) return;

    const checkConnectionQuality = async () => {
      try {
        const startTime = performance.now();
        const response = await fetch("/api/health", {
          method: "HEAD",
          cache: "no-cache",
        });
        const endTime = performance.now();
        const latency = endTime - startTime;

        if (latency > 3000) {
          toast.warning("Slow connection detected", {
            duration: 3000,
            position: "top-right",
          });
        }
      } catch (error) {
        // Silent fail for connection quality check
        console.debug("Connection quality check failed:", error.message);
      }
    };

    const interval = setInterval(checkConnectionQuality, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [isOnline]);

  const value = {
    isOnline,
    connectionType,
    connectionSpeed,
    lastSeen,
    checkConnectivity: async () => {
      try {
        const response = await fetch("/api/health", {
          method: "HEAD",
          cache: "no-cache",
        });
        return response.ok;
      } catch (error) {
        return false;
      }
    },
  };

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
};
